import React, { useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Calendar, Check, MessageCircle, Phone, Mail, Clock } from "lucide-react";
import Seo from "../components/Seo";
import { businessLd, breadcrumbLd } from "../components/StructuredData";
import { useInspiration } from "../hooks/useStore";

const BUDGET_BANDS = [
  "Under NPR 1,00,000",
  "NPR 1,00,000 – 3,00,000",
  "NPR 3,00,000 – 7,00,000",
  "NPR 7,00,000 – 15,00,000",
  "Over NPR 15,00,000",
  "Not sure yet — discuss in consultation",
];

const TIMELINES = [
  "ASAP / next 4 weeks",
  "1 – 3 months",
  "3 – 6 months",
  "Just exploring",
];

const SCOPES = [
  "Single piece (e.g. one sofa, one wardrobe)",
  "One room",
  "Multiple rooms",
  "Full home / project",
  "Office / commercial",
];

const CONTACT_PREF = ["WhatsApp", "Phone Call", "Email"];

export default function BookPage() {
  const { list } = useInspiration();
  const location = useLocation();
  const fromInspiration = location.state?.fromInspiration;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    scope: "",
    budget: "",
    timeline: "",
    contactPref: "WhatsApp",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const summary = useMemo(() => {
    const lines = [
      `Hello Ananda,`,
      ``,
      `I'd like to book a free consultation.`,
      ``,
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.phone && `Phone / WhatsApp: ${form.phone}`,
      form.location && `Location: ${form.location}`,
      form.scope && `Scope: ${form.scope}`,
      form.budget && `Budget band: ${form.budget}`,
      form.timeline && `Timeline: ${form.timeline}`,
      `Preferred contact: ${form.contactPref}`,
      form.message && `\nDetails: ${form.message}`,
    ].filter(Boolean);

    if (list.length) {
      lines.push("", "Inspiration list:");
      list.forEach((i, n) => {
        lines.push(`${n + 1}. ${i.name}${i.finish ? ` (${i.finish})` : ""}`);
      });
    }
    return lines.join("\n");
  }, [form, list]);

  const waLink = `https://wa.me/9779841488271?text=${encodeURIComponent(summary)}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  const jsonLd = [
    businessLd(),
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Book a Consultation", path: "/book" },
    ]),
  ];

  return (
    <>
      <Seo
        title="Book a Free Consultation"
        description="Book a free 30-minute interior design consultation with Ananda Paudel. Discuss your project, budget, and timeline. No obligation, in person or over WhatsApp."
        path="/book"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-cream-50 pt-20 sm:pt-24">
        <div className="bg-cream-100 border-b border-cream-200 py-10 sm:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-2">
              Stage 01 · Consulting
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-matte-900 font-light">
              Book a Free Consultation
            </h1>
            <p className="font-sans text-sm sm:text-base text-matte-500 mt-3 max-w-2xl leading-relaxed">
              30 minutes, in person or over WhatsApp. Tell Ananda about your
              space, your taste, and your budget — he'll tell you, plainly,
              what's worth doing.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            <div className="lg:col-span-2">
              <h2 className="font-serif text-2xl sm:text-3xl text-matte-900 font-light mb-5">
                What happens after you submit
              </h2>
              <div className="space-y-5 mb-10">
                {[
                  {
                    n: "1",
                    t: "Ananda reads your brief",
                    d: "Usually within a few hours during working hours (Sun–Fri, 9–7).",
                  },
                  {
                    n: "2",
                    t: "He reaches out the way you prefer",
                    d: "WhatsApp, phone, or email — whichever you ticked.",
                  },
                  {
                    n: "3",
                    t: "30-minute consultation",
                    d: "Site visit, video call, or WhatsApp chat. No fee, no obligation.",
                  },
                  {
                    n: "4",
                    t: "If it makes sense, we move to design",
                    d: "Otherwise, you walk away with honest advice. That's it.",
                  },
                ].map((s) => (
                  <div key={s.n} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-walnut-500 text-cream-100 font-sans text-sm flex items-center justify-center flex-shrink-0 font-medium">
                      {s.n}
                    </div>
                    <div>
                      <p className="font-serif text-base text-matte-900 mb-0.5">{s.t}</p>
                      <p className="font-sans text-sm text-matte-500 leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: <MessageCircle size={16} />,
                    label: "WhatsApp (fastest)",
                    value: "+977-9841488271",
                    href: "https://wa.me/9779841488271",
                  },
                  {
                    icon: <Phone size={16} />,
                    label: "Phone",
                    value: "+977-9841488271",
                    href: "tel:+9779841488271",
                  },
                  {
                    icon: <Mail size={16} />,
                    label: "Email",
                    value: "ananda.paudel.work@gmail.com",
                    href: "mailto:ananda.paudel.work@gmail.com",
                  },
                  {
                    icon: <Clock size={16} />,
                    label: "Hours",
                    value: "Sun – Fri · 9:00 – 19:00",
                  },
                ].map((it) => (
                  <div key={it.label} className="flex gap-3">
                    <div className="w-9 h-9 bg-walnut-100 text-walnut-500 rounded-full flex items-center justify-center flex-shrink-0">
                      {it.icon}
                    </div>
                    <div>
                      <p className="font-sans text-xs uppercase tracking-wider text-matte-400 mb-0.5">
                        {it.label}
                      </p>
                      {it.href ? (
                        <a
                          href={it.href}
                          className="font-sans text-sm text-matte-700 hover:text-walnut-600 transition-colors"
                        >
                          {it.value}
                        </a>
                      ) : (
                        <p className="font-sans text-sm text-matte-700">{it.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-3">
              {sent ? (
                <div className="bg-white border border-cream-200 rounded-sm p-10 text-center">
                  <div className="w-16 h-16 bg-walnut-100 text-walnut-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check size={28} />
                  </div>
                  <h3 className="font-serif text-3xl text-matte-900 mb-3">
                    Consultation request received
                  </h3>
                  <p className="font-sans text-sm text-matte-500 leading-relaxed max-w-md mx-auto mb-8">
                    Ananda will personally reach out via your preferred
                    channel within a few hours during working hours. If
                    you'd like to skip the wait, message him on WhatsApp now
                    — your details are pre-filled.
                  </p>
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white font-sans text-sm tracking-wide px-6 py-3.5 hover:bg-[#1ebe5d] transition-colors rounded-sm"
                  >
                    Send the same message on WhatsApp
                  </a>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-white border border-cream-200 rounded-sm p-6 sm:p-10 space-y-5"
                >
                  {fromInspiration && list.length > 0 && (
                    <div className="bg-walnut-50 border border-walnut-200 rounded-sm p-4 mb-2">
                      <p className="font-sans text-xs tracking-widest text-walnut-600 uppercase mb-1">
                        Inspiration list attached
                      </p>
                      <p className="font-sans text-sm text-matte-600">
                        {list.length} reference design
                        {list.length === 1 ? "" : "s"} from your inspiration
                        list will be sent with this request.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                        Name *
                      </label>
                      <input
                        required
                        value={form.name}
                        onChange={set("name")}
                        className="w-full bg-cream-50 border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                        Phone / WhatsApp
                      </label>
                      <input
                        value={form.phone}
                        onChange={set("phone")}
                        className="w-full bg-cream-50 border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                        placeholder="+977-"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                        Email *
                      </label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        className="w-full bg-cream-50 border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                        City / area
                      </label>
                      <input
                        value={form.location}
                        onChange={set("location")}
                        className="w-full bg-cream-50 border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                        placeholder="e.g. Lalitpur, Pokhara"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                      Project scope
                    </label>
                    <select
                      value={form.scope}
                      onChange={set("scope")}
                      className="w-full bg-cream-50 border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                    >
                      <option value="">Select scope</option>
                      {SCOPES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                        Budget band
                      </label>
                      <select
                        value={form.budget}
                        onChange={set("budget")}
                        className="w-full bg-cream-50 border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                      >
                        <option value="">Select budget</option>
                        {BUDGET_BANDS.map((b) => (
                          <option key={b}>{b}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                        Timeline
                      </label>
                      <select
                        value={form.timeline}
                        onChange={set("timeline")}
                        className="w-full bg-cream-50 border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                      >
                        <option value="">Select timeline</option>
                        {TIMELINES.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                      Preferred contact
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {CONTACT_PREF.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setForm({ ...form, contactPref: c })}
                          className={`font-sans text-sm px-4 py-2 border rounded-sm transition-all ${
                            form.contactPref === c
                              ? "border-matte-900 bg-matte-900 text-cream-100"
                              : "border-cream-300 text-matte-600 hover:border-walnut-400"
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                      Tell Ananda about your project
                    </label>
                    <textarea
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      className="w-full bg-cream-50 border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm resize-none"
                      placeholder="Style references, room dimensions, what you've tried, photos you can share over WhatsApp — anything helps."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-matte-900 text-cream-100 font-sans text-sm tracking-wider py-4 hover:bg-walnut-700 transition-colors rounded-sm"
                  >
                    <Calendar size={16} />
                    Request a Free Consultation
                  </button>
                  <p className="font-sans text-xs text-matte-400 text-center">
                    Or message{" "}
                    <a
                      href="https://wa.me/9779841488271"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-walnut-500 hover:underline"
                    >
                      Ananda directly on WhatsApp
                    </a>
                    .
                  </p>
                </form>
              )}

              <p className="font-sans text-xs text-matte-400 mt-6 text-center">
                Prefer the standard contact form?{" "}
                <Link to="/contact" className="text-walnut-500 hover:underline">
                  Use the contact page instead
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
